/**
 * "Should this render?" — asked once, answered by both gates.
 *
 * A feature has two independent gates and they are easy to confuse:
 *
 * | | asks | resolved | when it says no |
 * |---|---|---|---|
 * | **brand** | does *this app* have it? | at build, from a profile | absent — no trace |
 * | **entitlement** | may *this account* use it? | per request, from rows | locked, an upsell |
 *
 * **The brand is applied first, and its no is stronger.** Off in the brand
 * means gone: no lock, no upsell, no mention, because an app that does not sell
 * a feature must not advertise one. Off in the *entitlement* is the case that
 * still shows something, which is why `available` and `locked` are two
 * questions here rather than one boolean.
 *
 * ## Why this module exists rather than two calls at each site
 *
 * `BRAND.enabled(id) && can(cap)` written at twenty call sites is twenty
 * chances to write one half and forget the other — and forgetting the *brand*
 * half is invisible in development, where no profile is loaded and everything
 * is on. The registry already records which capability each feature pairs with;
 * this is the one place that pairing is spent.
 *
 * Nothing here is enforcement. Both gates are chrome: the server checks again
 * on every route that matters, and a client the browser can edit was never the
 * thing standing between an account and a feature.
 */

import { can, useEntitlements } from "./entitlements";
import { BRAND } from "./brand-current";
import { featureById, type FeatureId } from "./brand-registry";

/**
 * Whether a feature should be on screen at all.
 *
 * False when the brand does not have it, or when the account may not use it —
 * the caller that wants to tell those apart uses `locked` below, and most do
 * not: for a control, both answers mean "do not draw it".
 *
 * Synchronous, so it is usable outside React. Entitlements are **permissive
 * until the server answers**, which is `entitlements.ts`'s deliberate bargain:
 * a control that flashed locked and then unlocked reads as the app taking
 * something away.
 */
export function available(id: FeatureId): boolean {
  if (!BRAND.enabled(id)) return false;
  const capability = featureById(id)?.capability;
  return capability === undefined || can(capability);
}

/**
 * Whether this account is the reason a feature is unavailable.
 *
 * True only when the brand *has* the feature and the entitlement withholds it —
 * the one state where something may be drawn locked, with a reason. A feature
 * the brand does not have is never locked; it does not exist here.
 */
export function locked(id: FeatureId): boolean {
  if (!BRAND.enabled(id)) return false;
  const capability = featureById(id)?.capability;
  return capability !== undefined && !can(capability);
}

/** What this brand calls a feature. The registry's label unless it renamed it. */
export function labelOf(id: FeatureId): string {
  return BRAND.label(id);
}

/**
 * `available`, as a hook, for chrome that must re-render when the answer moves.
 *
 * The brand half cannot change while the app runs — it was decided by the build
 * — so this exists entirely for the entitlement half, which arrives after a
 * fetch and changes again when an admin switches the account being acted as.
 * A component reading the plain function would render once against a permissive
 * default and never correct itself.
 */
export function useAvailable(id: FeatureId): boolean {
  const { can: allows } = useEntitlements();
  if (!BRAND.enabled(id)) return false;
  const capability = featureById(id)?.capability;
  return capability === undefined || allows(capability);
}
